import { useLayoutEffect, useRef, useState } from 'react';
import type { BrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import { markRouteChange, markSpaNavigationStart } from '@hotjar/performance';
import { browserHistory } from '../browser-history';

export interface BrowserRouterProps {
  basename?: string;
  children?: React.ReactNode;
}

/**
 * A <Router> for use in web browsers. Provides the cleanest URLs.
 * This is more or less a copy of the `BrowserRouter` from `react-router-dom` except
 * we use our own `history` object instead of the one provided by `react-router-dom`.
 * We do this so that we can trigger react router state changes from angular.
 */
export function BrowserRouter({ basename, children }: BrowserRouterProps): JSX.Element {
  const historyRef = useRef<BrowserHistory>();
  if (historyRef.current == null) {
    historyRef.current = browserHistory;
  }

  const history = historyRef.current;
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(
    () =>
      history.listen((event) => {
        setState(event);

        // On every route change, we want to set a new performance baseline to measure against
        if (event.action === 'PUSH' || event.action === 'POP') {
          markRouteChange();
          markSpaNavigationStart();
        }
      }),
    [history],
  );

  return (
    <Router
      basename={basename}
      // Disabling this here since this code is a direct copy from the BrowserRouter implementation found in
      // react-router-dom with the only change being where we get browserHistory from.
      // eslint-disable-next-line react/no-children-prop
      children={children}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    />
  );
}
